/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Page for the to search for downloads by client ID
 * @module Epic.AppOrchard.DownloadSearch
 */

import { Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { useSiteInfoState } from "ao/state/siteInfo";
import { ITopLevelComponentProps } from "ao/types";
import { BackLinkType } from "ao/types/showroom";
import { isLocalhost } from "ao/utils/helpers";
import React, { FunctionComponent, memo, useCallback, useState } from "react";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { getDownloadAppUrl } from "routes/downloadApp";
import { getShowroomAppAdminUrl } from "routes/showroomListingAdmin";
import { IGenericAPIResponse } from "../../data";
import { isInFlight } from "../../utils/useAsyncHelpers";
import { AOLink, AOTooltip, SearchInput, StatusAlert } from "../Core";
import { BodyContent } from "../Frame";
import { findAppWithClientId } from "./data/data";

const DownloadSearch: FunctionComponent<ITopLevelComponentProps> = memo((props) => {
	const { userSecurity } = props;
	const [localSearch, setLocalSearch] = useState<string>("");
	const [loadError, setLoadError] = useState<string>("");

	const navigate = useNavigate();
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const showroomRedirectMessagePrefix = "SHOWROOM_REDIRECT_";

	const handleSuccess = useCallback(
		(response: IGenericAPIResponse): void => {
			if (response.success) {
				const returnParams = `&returnTitle=${BackLinkType.ToDownload}`;

				if (response.message.indexOf(showroomRedirectMessagePrefix) === 0) {
					const appId = response.message.split(showroomRedirectMessagePrefix)[1];

					if (isLocalhost() || siteInfo.isAdminSite) {
						navigate(`${getShowroomAppAdminUrl(appId)}${returnParams}`, { replace: true });
					} else {
						window.location.href = `${siteInfo.showroomUrl}listing?appId=${appId}${returnParams}`;
					}
				} else {
					navigate(`${getDownloadAppUrl(response.message)}${returnParams}`);
				}
			} else {
				setLoadError(response.message);
			}
		},
		[navigate],
	);

	const [searchingState, findApp] = useAsync(findAppWithClientId, {
		displayName: "findApp",
		onSuccess: handleSuccess,
		onFailure: () => setLoadError("An error occurred. Please try again later."),
	});

	const handleEnterKeyPress = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>): void => {
			if (e.key === "Enter" && localSearch) {
				findApp(encodeURIComponent(localSearch));
			}
		},
		[findApp, localSearch],
	);

	const isSearching = isInFlight(searchingState);

	return (
		<BodyContent background="none">
			<VStack spacing="10px" alignItems="flex-start">
				<Heading fontSize="1.75rem" fontWeight="400" color="green.600" pt="20px">
					Find a product by client ID
				</Heading>
				<Flex width={{ base: "100%", md: "60%", lg: "50%" }}>
					<SearchInput
						placeholder="Product client ID..."
						onChangeCallback={(e) => setLocalSearch(e)}
						inputProps={{
							onKeyUp: handleEnterKeyPress,
						}}
						showSearchButton
						fullyDisabled={isSearching}
						onSearchClicked={() => findApp(encodeURIComponent(localSearch))}
						onClearClicked={() => setLocalSearch("")}
					/>
					<AOTooltip
						helptext="You can obtain a product's client ID from the vendor."
						boxProps={{ ml: "0.5em", mt: "10px" }}
					>
						<BsFillQuestionCircleFill />
					</AOTooltip>
				</Flex>
				<Text fontWeight="500">
					The developer provides all the information about their products and is responsible for the
					safety and performance of their software.
				</Text>
				{loadError && <StatusAlert status="error" message={loadError} />}

				{!userSecurity.isEpicUser && !userSecurity.isVendorUser && (
					<Text fontWeight="400" fontSize="1.25rem" pt="20px">
						See <AOLink url={"/Picked"}>Picked Apps</AOLink> for a full list of apps your
						organization has requested or downloaded. You may need to log in again to access this
						page.
					</Text>
				)}
			</VStack>
		</BodyContent>
	);
});

export default DownloadSearch;
